
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { getObjectByPath } from '../../utils';
import * as XLSX from 'xlsx';
import { FieldRendererProps } from './FieldTableTypes';
import { Daybook } from '../../generated/graphql';
import { FieldTableSettingsContext, FieldTableSettingsContextValue } from '../../contexts/field-table-settings-context';
import { ExchangeRatesContext, ExchangeRatesContextValue } from '../../contexts/exchange-rates-context';
import { useDaybookContext } from '../../contexts/Daybook/DayBookContext';
import {get} from 'immutadot';
import { FieldTableColumnProps } from './FieldTableTypes';

type FieldTableExportRawProps<Type> = {
    tableValues: Array<Type>|null,
    columns:Array<FieldTableColumnProps<Type>>
    columnTypesContext:any
    exchangeRateContext:any
    getRowProps:any,
    excludedIds:any,
    name:string,
    config:Daybook
}

function getExcelColumnLetter(colIndex) {
    let letter = '';
    while (colIndex > 0) {
        const remainder = (colIndex - 1) % 26;
        letter = String.fromCharCode(65 + remainder) + letter; // 65 is the ASCII value for 'A'
        colIndex = Math.floor((colIndex - 1) / 26);
    }
    return letter;
}

export function useFieldTableExportRaw<Type>(props:FieldTableExportRawProps<Type>) {
    const exportData = useCallback((rows=null) => {
        const values = rows?rows:props.tableValues;
        console.log('run exportdata',values);
        if(!rows) return;
        const pixPerLetter = 7;
        const eData = [];
        const columns = props.columns;
        const worksheet = {'!ref': 'A1:'};
        let col = 1;
        const colwidths = [];
        columns.forEach((column) => {
            const header = column.headerRaw ? column.headerRaw : column.header;
            if (column.noExport) return;
            if (column.dataType && props.columnTypesContext.register[column.dataType]) {
                const columnDef = props.columnTypesContext.register[column.dataType];
                if (columnDef.noExport) return;
            }
            const columnLetter = getExcelColumnLetter(col);
            worksheet[columnLetter + 1] = {t: 's', v: header};
            // worksheet[String.fromCharCode(64 + col) + 1] = {t: 's', v: header};
            colwidths.push({wpx: header.toString().length});
            col++;
        });
        worksheet['!ref'] = 'A1:' + String.fromCharCode(64 + col) + '1';
        eData.push(columns.map((column) => column.header));
        let rowIdx = 2;
        let lastCell = '';
        rows.forEach((row) => {
            if (props.excludedIds[row?.id]) return;
            const rowProps = props.getRowProps ? props.getRowProps({data: row,config:props.config}) : false;
            let col = 1;
            columns.forEach((column) => {
                const cell: {t: string, v: any, f: any, w: any, z: any, l: any, width?: number} = {t: 's', v: undefined, f: undefined, w: undefined, z: undefined, l: undefined};
                const columnLetter = getExcelColumnLetter(col);
                if (column.noExport) return;
                if (column.dataType && props.columnTypesContext.register[column.dataType]) {
                    const columnDef = props.columnTypesContext.register[column.dataType];
                    if (columnDef.noExport) return;
                    if (columnDef.renderExport) {
                        const ftProps: FieldRendererProps<Type,any> = {
                            field: {
                                name: props.name + '.' + column.accessor,
                                value: getObjectByPath(row, column.accessor),
                                onChange: null,
                                fill: true
                            },
                            options: column.options,
                            row: row,
                            rowName: '',
                            rowProps: rowProps,
                            columnData: column.data,
                            calcExchangeRate: props.exchangeRateContext.calcExchangeRate,
                            defaultCurrency: props.exchangeRateContext.targetCurrency
                        };

                        columnDef.renderExport(cell, ftProps);
                        console.log('cell', cell);
                    }
                }
                if(cell.width) {
                    colwidths[col - 1].wpx = Math.max(colwidths[col - 1].wpx, cell.width);
                    delete(cell.width);
                } else {
                    if (cell.v) {
                        colwidths[col - 1].wpx = Math.max(colwidths[col - 1].wpx, cell.v.toString().length);
                    }
                }
                // lastCell = String.fromCharCode(64 + col++) + rowIdx;
                lastCell = columnLetter + rowIdx;
                worksheet[lastCell] = cell;
                col++;
            });
            rowIdx++;
        });
        worksheet['!ref'] = 'A1:' + lastCell;
        colwidths.forEach(v => {
            v.wpx = v.wpx * pixPerLetter;
        });
        worksheet['!cols'] = colwidths;
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, worksheet, "SheetJS");
        XLSX.writeFile(wb, "sheetjs.xlsx");
    }, [props.tableValues, props.excludedIds, props.exchangeRateContext?.targetCurrency?.glyph,props.columnTypesContext,props.name]);

    return {exportData:exportData};
}

type FieldTableExportProps<Type> = {
    exportColumns?:Array<any>|null,
    columns:Array<any>,
    getRowProps?:any|null,
    excludedIds?:any|null,
    name:string,
    form?:any,
    loading?:boolean,
    values:Array<Type>|null,
    defaultSort?: { name: string, asc: boolean }[]
}

export function useFieldTableExport<Type>(props:FieldTableExportProps<Type>) {
    const exchangeRateContext = useContext<ExchangeRatesContextValue|null>(ExchangeRatesContext);
    const [sortState, setSortState] = useState(props.defaultSort || []);
    const [excludedIds, setExcludedIds] = useState({});
    const columnTypesContext = useContext<FieldTableSettingsContextValue|null>(FieldTableSettingsContext);
    const noResults = false;
    // tablevalues is
    /*if (props.loading) {
        tableValues = [];
    } else {
        if(props.values) {
            tableValues = props.values;
        } else {
            if(props.values) {
                tableValues = getObjectByPath(props.form?.values, props.name);
            }
        }
    }*/
    const [openRows, setOpenRows] = useState({});
    const [sortedOrder, setSortedOrder] = useState<Array<number>>([]);
    const [selectedAll, setSelectedAll] = useState(false);
    const [openedAll, setOpenedAll] = useState(false);
    const [focusedRow, setFocusedRow] = useState({});
    const config = useDaybookContext();
    /*if(!tableValues) return;
    let so:Array<number> = [];
    if (props.loading || noResults) {
        for (let i = 0; i < tableValues.length; i++) {
            so.push(i);
        }
    } else {
        // tableValues may be out of date, but we don't entirely care...
        if (sortState.length) {
            const sortValues = tableValues.map((e, i) => ({ind: i, val: e}));
            sortValues.sort(
                (x, y) => {
                    let fieldA = get(x.val, sortState[0].name);
                    let fieldB = get(y.val, sortState[0].name);
                    let ret = 0;
                    if (sortState[0].asc) {
                        ret = fieldA > fieldB ? 1 : (fieldA === fieldB ? 0 : -1);
                    } else {
                        ret = fieldA < fieldB ? 1 : (fieldA === fieldB ? 0 : -1);
                    }
                    if (!ret && sortState.length > 1) {
                        fieldA = get(x.val, sortState[1].name);
                        fieldB = get(y.val, sortState[1].name);
                        if (sortState[1].asc) {
                            ret = fieldA > fieldB ? 1 : (fieldA === fieldB ? 0 : -1);
                        } else {
                            ret = fieldA < fieldB ? 1 : (fieldA === fieldB ? 0 : -1);
                        }
                    }
                    return ret;
                }
            );
            so = sortValues.map((e) => (e.ind));
        } else {
            so = (tableValues ? Array.from(tableValues.keys()) : []);
        }
    }
    setSortedOrder(so);*/


    const exportData = useFieldTableExportRaw({
        tableValues: props.values,
        columns:props.exportColumns?props.exportColumns:props.columns,
        columnTypesContext:columnTypesContext,
        exchangeRateContext:exchangeRateContext,
        getRowProps:props.getRowProps,
        excludedIds:excludedIds,
        name:props.name??'',
        config:config
    });
    return exportData;
}
